import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';
import { storableError } from '../../util/errors';
import { orgListingsAPI, orgCalendarAPI, assignmentsAPI, orgTeamAPI } from '../../api';
import { calendarInvitesAPI } from '../../api/clients/calendarInvitesAPI';
import config from '../../config';
import { PAGE_TYPE_QUERY, PAGE_TYPE } from '../../util/listingType';

// ================ Action types ================ //

export const SET_ORG_ID = 'app/OrgCalendarPage/SET_ORG_ID';

export const FETCH_CALENDAR_EVENTS_REQUEST = 'app/OrgCalendarPage/FETCH_CALENDAR_EVENTS_REQUEST';
export const FETCH_CALENDAR_EVENTS_SUCCESS = 'app/OrgCalendarPage/FETCH_CALENDAR_EVENTS_SUCCESS';
export const FETCH_CALENDAR_EVENTS_ERROR = 'app/OrgCalendarPage/FETCH_CALENDAR_EVENTS_ERROR';

export const CREATE_USER_EVENT_REQUEST = 'app/OrgCalendarPage/CREATE_USER_EVENT_REQUEST';
export const CREATE_USER_EVENT_SUCCESS = 'app/OrgCalendarPage/CREATE_USER_EVENT_SUCCESS';
export const CREATE_USER_EVENT_ERROR = 'app/OrgCalendarPage/CREATE_USER_EVENT_ERROR';

export const UPDATE_USER_EVENT_REQUEST = 'app/OrgCalendarPage/UPDATE_USER_EVENT_REQUEST';
export const UPDATE_USER_EVENT_SUCCESS = 'app/OrgCalendarPage/UPDATE_USER_EVENT_SUCCESS';
export const UPDATE_USER_EVENT_ERROR = 'app/OrgCalendarPage/UPDATE_USER_EVENT_ERROR';

export const DELETE_USER_EVENT_REQUEST = 'app/OrgCalendarPage/DELETE_USER_EVENT_REQUEST';
export const DELETE_USER_EVENT_SUCCESS = 'app/OrgCalendarPage/DELETE_USER_EVENT_SUCCESS';
export const DELETE_USER_EVENT_ERROR = 'app/OrgCalendarPage/DELETE_USER_EVENT_ERROR';

// Delete tour schedule actions
export const DELETE_TOUR_SCHEDULE_REQUEST = 'app/OrgCalendarPage/DELETE_TOUR_SCHEDULE_REQUEST';
export const DELETE_TOUR_SCHEDULE_SUCCESS = 'app/OrgCalendarPage/DELETE_TOUR_SCHEDULE_SUCCESS';
export const DELETE_TOUR_SCHEDULE_ERROR = 'app/OrgCalendarPage/DELETE_TOUR_SCHEDULE_ERROR';

// ================ Reducer ================ //

const initialState = {
  orgId: null,
  fetchInProgress: false,
  fetchLoaded: false,
  fetchError: null,
  events: [],
  listingRefs: [],
  createUserEventInProgress: false,
  createUserEventError: null,
  updateUserEventInProgress: false,
  updateUserEventError: null,
  deleteUserEventInProgress: false,
  deleteUserEventError: null,
  // Tour schedule delete state
  deleteTourScheduleInProgress: false,
  deleteTourScheduleError: null,
  // Create-event participant options (the org's team)
  participantOptions: [],
  // Invite records per event id: who was invited and what they answered
  inviteState: {},
};

export const SET_PARTICIPANT_OPTIONS = 'app/OrgCalendarPage/SET_PARTICIPANT_OPTIONS';
export const setParticipantOptions = options => ({
  type: SET_PARTICIPANT_OPTIONS,
  payload: options,
});

export default function orgCalendarPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_ORG_ID:
      // Switching orgs (same mounted component, different :orgId in the URL)
      // must drop the previous org's data so it can't flash under the new
      // org while its fetch is in flight. Same orgId → keep state so a
      // revisit/refetch stays stale-while-revalidate.
      return payload === state.orgId ? state : { ...initialState, orgId: payload };
    case SET_PARTICIPANT_OPTIONS:
      return { ...state, participantOptions: payload };
    case FETCH_CALENDAR_EVENTS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_CALENDAR_EVENTS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fetchLoaded: true,
        events: payload.events,
        listingRefs: payload.listingRefs,
        inviteState: payload.inviteState || {},
      };
    case FETCH_CALENDAR_EVENTS_ERROR:
      console.error(payload);
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };

    case CREATE_USER_EVENT_REQUEST:
      return { ...state, createUserEventInProgress: true, createUserEventError: null };
    case CREATE_USER_EVENT_SUCCESS:
      return {
        ...state,
        createUserEventInProgress: false,
        events: [...state.events, payload],
      };
    case CREATE_USER_EVENT_ERROR:
      return { ...state, createUserEventInProgress: false, createUserEventError: payload };

    case UPDATE_USER_EVENT_REQUEST:
      return { ...state, updateUserEventInProgress: true, updateUserEventError: null };
    case UPDATE_USER_EVENT_SUCCESS:
      return {
        ...state,
        updateUserEventInProgress: false,
        events: state.events.map(e => (e.id === payload.id ? payload : e)),
      };
    case UPDATE_USER_EVENT_ERROR:
      return { ...state, updateUserEventInProgress: false, updateUserEventError: payload };

    case DELETE_USER_EVENT_REQUEST:
      return { ...state, deleteUserEventInProgress: true, deleteUserEventError: null };
    case DELETE_USER_EVENT_SUCCESS:
      return {
        ...state,
        deleteUserEventInProgress: false,
        events: state.events.filter(e => e.id !== payload),
      };
    case DELETE_USER_EVENT_ERROR:
      return { ...state, deleteUserEventInProgress: false, deleteUserEventError: payload };

    // Delete tour schedule
    case DELETE_TOUR_SCHEDULE_REQUEST:
      return { ...state, deleteTourScheduleInProgress: true, deleteTourScheduleError: null };
    case DELETE_TOUR_SCHEDULE_SUCCESS:
      return {
        ...state,
        deleteTourScheduleInProgress: false,
        events: state.events.filter(e => e.id !== payload),
      };
    case DELETE_TOUR_SCHEDULE_ERROR:
      return { ...state, deleteTourScheduleInProgress: false, deleteTourScheduleError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchCalendarEventsRequest = () => ({
  type: FETCH_CALENDAR_EVENTS_REQUEST,
});

export const fetchCalendarEventsSuccess = (events, listingRefs, inviteState = {}) => ({
  type: FETCH_CALENDAR_EVENTS_SUCCESS,
  payload: { events, listingRefs, inviteState },
});

export const fetchCalendarEventsError = error => ({
  type: FETCH_CALENDAR_EVENTS_ERROR,
  error: true,
  payload: error,
});

export const createUserEventRequest = () => ({ type: CREATE_USER_EVENT_REQUEST });
export const createUserEventSuccess = event => ({ type: CREATE_USER_EVENT_SUCCESS, payload: event });
export const createUserEventError = error => ({ type: CREATE_USER_EVENT_ERROR, payload: error });

export const updateUserEventRequest = () => ({ type: UPDATE_USER_EVENT_REQUEST });
export const updateUserEventSuccess = event => ({ type: UPDATE_USER_EVENT_SUCCESS, payload: event });
export const updateUserEventError = error => ({ type: UPDATE_USER_EVENT_ERROR, payload: error });

export const deleteUserEventRequest = () => ({ type: DELETE_USER_EVENT_REQUEST });
export const deleteUserEventSuccess = eventId => ({ type: DELETE_USER_EVENT_SUCCESS, payload: eventId });
export const deleteUserEventError = error => ({ type: DELETE_USER_EVENT_ERROR, payload: error });

// Delete tour schedule action creators
export const deleteTourScheduleRequest = () => ({ type: DELETE_TOUR_SCHEDULE_REQUEST });
export const deleteTourScheduleSuccess = eventId => ({ type: DELETE_TOUR_SCHEDULE_SUCCESS, payload: eventId });
export const deleteTourScheduleError = error => ({ type: DELETE_TOUR_SCHEDULE_ERROR, payload: error });

// ================ Helpers ================ //

// Transform user event to FullCalendar event format
const userEventToCalendarEvent = (event) => {
  return {
    id: event.id,
    title: event.title || 'Untitled Event',
    start: event.start ? new Date(event.start) : null,
    end: event.end ? new Date(event.end) : null,
    allDay: event.allDay || false,
    extendedProps: {
      type: 'user-event',
      description: event.description,
      meeting: event.meeting,
      location: event.location?.address || event.location,
      timeZone: event.timeZone,
      attendees: event.attendees || [],
    },
  };
};

// Transform a Live Room exception (episode) into a calendar event.
// Lives under the assignment overlay — the org can see *when* their
// linked creator's episodes happen and click through to the Live Room
// for details. The event is read-only on the calendar (no delete/edit
// affordances), tagged provenance.kind='rented' so the modal can
// render the right copy + "Rented from <creator>" badge.
const assignedEpisodeToEvent = (exception, room, index) => {
  // publicData.exceptions entries come back from the integration SDK
  // as JSON:API resource objects — `start` / `end` / `title` live
  // under `attributes`. Fall back to the top level for older entries
  // that may have been written before that shape was finalized.
  const attrs = exception?.attributes || exception || {};
  const startTs = attrs.start ? parseInt(attrs.start, 10) : null;
  const endTs = attrs.end ? parseInt(attrs.end, 10) : null;
  const title =
    attrs.title ||
    attrs.seats?.[0]?.label ||
    room.liveRoomTitle ||
    'Episode';
  return {
    id: `assigned-episode-${room.assignmentId}-${index}`,
    title,
    start: startTs ? new Date(startTs) : null,
    end: endTs ? new Date(endTs) : null,
    allDay: false,
    extendedProps: {
      type: 'assigned-episode',
      assignmentId: room.assignmentId,
      liveRoomId: room.liveRoomId,
      liveRoomTitle: room.liveRoomTitle,
      creatorId: room.creatorId,
      creatorName: room.creatorName,
      expiresAt: room.expiresAt,
      // Spread the same fields the existing modal reads on episode /
      // user-event types so it renders meeting link, description,
      // location, etc. without a custom branch. Host-only meeting
      // fields are stripped server-side before they get here.
      meeting: attrs.meeting || null,
      description: attrs.description || null,
      location: attrs.location || null,
      timeZone: attrs.timeZone || null,
      duration: attrs.duration || null,
      access: attrs.access || null,
      provenance: {
        kind: 'rented',
        assignmentId: room.assignmentId,
        creatorId: room.creatorId,
        creatorName: room.creatorName,
        liveRoomId: room.liveRoomId,
        liveRoomTitle: room.liveRoomTitle,
        expiresAt: room.expiresAt,
      },
    },
  };
};

// Transform tour schedule to FullCalendar event format for Production Pages
const tourScheduleToEvent = (schedule, listing, index) => {
  const listingTitle = listing?.attributes?.title || 'Untitled';
  const listingId = listing?.id?.uuid;
  const listingSlug = listing?.attributes?.title?.toLowerCase()?.replace(/\s+/g, '-') || 'page';

  // Tour schedules may have different date formats
  const startDate = schedule?.startDate?.date || schedule?.startDate || null;
  const endDate = schedule?.endDate?.date || schedule?.endDate || null;
  const location = schedule?.location?.selectedPlace?.address || schedule?.location?.address || '';

  return {
    id: `page-${listingId}-${index}`,
    title: listingTitle,
    start: startDate ? new Date(startDate) : null,
    end: endDate ? new Date(endDate) : null,
    allDay: true,
    extendedProps: {
      type: PAGE_TYPE,
      listingId,
      listingTitle,
      listingSlug,
      location,
      scheduleIndex: index,
    },
  };
};

// ================ Thunks ================ //

export const setOrgId = (orgId) => ({
  type: SET_ORG_ID,
  payload: orgId,
});

// Helper to update an org's calendarEvents via the server proxy.
// Writes always go through orgCalendarAPI so the server (Integration SDK)
// mutates the OWNER's profile regardless of who the caller is — admins /
// managers can edit even though the data isn't on their own user record.
const writeOrgCalendarEvents = async (orgId, newCalendarEvents) => {
  await orgCalendarAPI.updateEvents(orgId, newCalendarEvents);
};

export const fetchCalendarEvents = orgId => async dispatch => {
  dispatch(fetchCalendarEventsRequest());
  dispatch(setOrgId(orgId));

  try {
    const allEvents = [];
    const allListingRefs = [];

    // Org-scoped listing read via Integration SDK proxy — works for both
    // owner and non-owner team members.
    const showResponse = await orgListingsAPI.query(orgId, {
      pub_type: PAGE_TYPE_QUERY,
      states: 'draft,pendingApproval,published,closed',
      include: 'images',
      'fields.image': 'variants.landscape-crop,variants.landscape-crop2x',
      'limit.images': 1,
    });

    const showListings = showResponse?.data || [];

    allListingRefs.push(...showListings.map(l => ({ id: l.id, type: l.type })));

    for (const listing of showListings) {
      const tourSchedules = listing?.attributes?.publicData?.tourSchedules || [];
      const events = tourSchedules.map((schedule, index) =>
        tourScheduleToEvent(schedule, listing, index)
      );
      allEvents.push(...events);
    }

    // Org-level calendarEvents live on owner.publicData.organizationProfiles
    // — proxy through the server so non-owner members can read them.
    const eventsResp = await orgCalendarAPI.listEvents(orgId);
    const orgCalendarEvents = eventsResp?.data?.data?.events || [];
    const inviteState = eventsResp?.data?.data?.invites || {};
    const userCalendarEvents = orgCalendarEvents.map(userEventToCalendarEvent);
    allEvents.push(...userCalendarEvents);

    // Rented overlay: pull every active assignment's Live Room
    // exceptions and surface them as read-only calendar events. Best-
    // effort — a failed fetch just skips the overlay rather than
    // bringing down the rest of the calendar.
    try {
      const rentedResp = await assignmentsAPI.getRentedEpisodes(orgId);
      const rooms = rentedResp?.data?.data?.rooms || [];
      for (const room of rooms) {
        (room.exceptions || []).forEach((exception, i) => {
          const event = assignedEpisodeToEvent(exception, room, i);
          if (event.start) allEvents.push(event);
        });
      }
    } catch (e) {
      console.warn('Rented episodes fetch failed:', e?.message || e);
    }

    dispatch(fetchCalendarEventsSuccess(allEvents, allListingRefs, inviteState));
  } catch (e) {
    dispatch(fetchCalendarEventsError(storableError(e)));
  }
};

/**
 * Participant search over the org's own audience, on the server, where the
 * tier rules live (server/api-util/calendarInvites.js). Returns options for
 * the picker; nothing is kept in state.
 */
export const searchParticipants = q => async (dispatch, getState) => {
  const { orgId } = getState().OrgCalendarPage;
  if (!orgId) return [];
  const res = await calendarInvitesAPI.searchOrgParticipants(orgId, q);
  return res?.data?.data?.options || [];
};

// Fetch the current calendarEvents list from the server. Used as the
// authoritative "before" snapshot when we build the next list (replaces
// the previous client-side read from currentUser.publicData, which only
// worked for owners).
const fetchCurrentCalendarEvents = async orgId => {
  const resp = await orgCalendarAPI.listEvents(orgId);
  return Array.isArray(resp?.data?.data?.events) ? resp.data.data.events : [];
};

// Create a new user event for this organization
export const createUserEvent = eventData => async (dispatch, getState) => {
  dispatch(createUserEventRequest());

  try {
    const { orgId } = getState().OrgCalendarPage;
    if (!orgId) throw new Error('Organization ID not set');

    const existingEvents = await fetchCurrentCalendarEvents(orgId);
    const updatedEvents = [...existingEvents, eventData];

    await writeOrgCalendarEvents(orgId, updatedEvents);

    const calendarEvent = userEventToCalendarEvent(eventData);
    dispatch(createUserEventSuccess(calendarEvent));
    await dispatch(fetchCalendarEvents(orgId));

    return calendarEvent;
  } catch (e) {
    dispatch(createUserEventError(storableError(e)));
    throw e;
  }
};

// Update an existing user event in the organization profile
export const updateUserEvent = (eventId, eventData) => async (dispatch, getState) => {
  dispatch(updateUserEventRequest());

  try {
    const { orgId } = getState().OrgCalendarPage;
    if (!orgId) throw new Error('Organization ID not set');

    const existingEvents = await fetchCurrentCalendarEvents(orgId);
    const updatedEvents = existingEvents.map(e =>
      e.id === eventId ? { ...e, ...eventData } : e
    );

    await writeOrgCalendarEvents(orgId, updatedEvents);

    const calendarEvent = userEventToCalendarEvent(eventData);
    dispatch(updateUserEventSuccess(calendarEvent));
    await dispatch(fetchCalendarEvents(orgId));

    return calendarEvent;
  } catch (e) {
    dispatch(updateUserEventError(storableError(e)));
    throw e;
  }
};

// Delete a user event from the organization profile
export const deleteUserEvent = eventId => async (dispatch, getState) => {
  dispatch(deleteUserEventRequest());

  try {
    const { orgId } = getState().OrgCalendarPage;
    if (!orgId) throw new Error('Organization ID not set');

    const existingEvents = await fetchCurrentCalendarEvents(orgId);
    const updatedEvents = existingEvents.filter(e => e.id !== eventId);

    await writeOrgCalendarEvents(orgId, updatedEvents);

    dispatch(deleteUserEventSuccess(eventId));
    await dispatch(fetchCalendarEvents(orgId));

    return eventId;
  } catch (e) {
    dispatch(deleteUserEventError(storableError(e)));
    throw e;
  }
};

// Delete a tour schedule from a show listing. Reads the listing + writes
// the update through the server proxy so admins/managers can do it even
// though the listing lives on the owner's account. Server enforces
// EDIT_ANY_SHOW + cross-checks publicData.organizationId.
export const deleteTourSchedule = (listingId, scheduleIndex) => async (dispatch, getState) => {
  dispatch(deleteTourScheduleRequest());

  try {
    const { orgId } = getState().OrgCalendarPage;
    if (!orgId) throw new Error('Organization ID not set');

    const showResponse = await orgListingsAPI.show(orgId, listingId);
    const listing = showResponse?.data;
    if (!listing) {
      throw new Error('Listing not found');
    }

    const existingSchedules = listing?.attributes?.publicData?.tourSchedules || [];
    const updatedSchedules = existingSchedules.filter((_, idx) => idx !== scheduleIndex);

    await orgListingsAPI.update(orgId, listingId, {
      publicData: { tourSchedules: updatedSchedules },
    });

    const eventId = `show-${listingId}-${scheduleIndex}`;
    dispatch(deleteTourScheduleSuccess(eventId));
    await dispatch(fetchCalendarEvents(orgId));

    return eventId;
  } catch (e) {
    dispatch(deleteTourScheduleError(storableError(e)));
    throw e;
  }
};

// Build the Create-Event participant options: team members first, then
// Audience Lists. Best-effort — a failure here must not block the calendar.
export const fetchParticipantOptions = orgId => async dispatch => {
  try {
    const teamResp = await orgTeamAPI.list(orgId).catch(() => null);
    const members = teamResp?.data?.members || teamResp?.data || [];
    // Whole audience lists are no longer offered here. Attaching a list did
    // nothing (no mail was ever sent for list participants) and the calendar
    // privacy ticket puts bulk invites out of scope for v1. Hidden rather than
    // wired up, per Yuko (1 Sep 2026), until "send invites" is an explicit,
    // separate action that cannot mass-mail a list by accident.
    const options = members.map(m => ({
      id: `team:${m.userId || m.id}`,
      type: 'team',
      label: m.name || m.displayName || m.email || 'Team member',
    }));
    dispatch(setParticipantOptions(options));
  } catch (e) {
    // ignore — participants are optional
  }
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  const { orgId } = params;

  // Ensure user is logged in
  await dispatch(fetchCurrentUser());

  // Fetch organization profiles (needed for DashboardWrapper)
  await dispatch(fetchOrganizationProfiles());

  // Participant options for the Create-Event modal (non-blocking).
  dispatch(fetchParticipantOptions(orgId));

  // Fetch calendar events for this organization
  return dispatch(fetchCalendarEvents(orgId));
};
